<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

import useResponsive from '@/composables/useResponsive';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { useProductsStore } from '@/stores/useProductsStore';
import { getTranslatedName } from '@/utils/utils';

const { isMobile } = useResponsive();

const metricsStore = useMetricsStore();
const { types } = storeToRefs(metricsStore);

const productsStore = useProductsStore();

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const selectedTypeIds = ref<string[]>([]);
const showAll = ref(false);
const maxVisibleTypes = isMobile.value ? 4 : 7;

onMounted(() => {
  if (
    selectedTypeIds.value.length > 0
      && types.value.findIndex(type => selectedTypeIds.value.includes(type.id)) >= maxVisibleTypes
  ) {
    showAll.value = true;
  }
});

const existingTypes = filters.value.selections.find(it => it.filterProperties.type === 'taxonomyTypes');
if (existingTypes?.selection?.length > 0) {
  selectedTypeIds.value = [...existingTypes.selection];
}

function toggleType(type: typeof types.value[number]) {
  const isAlreadySelected = selectedTypeIds.value.includes(type.id);

  if (isAlreadySelected) {
    selectedTypeIds.value = selectedTypeIds.value.filter(id => id !== type.id);
  }
  else {
    selectedTypeIds.value.push(type.id);
  }

  const typeFilter = filters.value.selections.find(it => it.filterProperties.type === 'taxonomyTypes');
  if (typeFilter) {
    typeFilter.selection = [...selectedTypeIds.value];
  }
  else {
    filters.value.selections.push({
      filterProperties: { type: 'taxonomyTypes' },
      selection: [...selectedTypeIds.value],
    });
  }

  const selectedTypes = types.value.filter(it => selectedTypeIds.value.includes(it.id));
  const hasNonMatching = selectedTypes.some(it => !it.isMatching);
  if (hasNonMatching) {
    filters.value.selections = filters.value.selections.filter(it => it.filterProperties.type !== 'taxonomyThemes');
    filters.value.metrics = filters.value.metrics.filter(it => it.type !== 'crossedTaxonomyCount');
  }

  filters.value.sorts = filters.value.sorts.filter((it: any) => it.type !== 'search');

  filtersStore.saveToStorage(filters.value);
  productsStore.fetchProductsList(filters.value);
}

const displayedTypes = computed(() => {
  return showAll.value ? types.value : types.value.slice(0, maxVisibleTypes);
});
</script>

<template>
  <ul id="types-list" role="listbox" class="options-list">
    <div
      v-for="(type, index) in displayedTypes"
      :key="type.id"
      class="option-row"
      tabindex="0"
    >
      <li
        role="option"
        tabindex="0"
        class="option"
        :aria-selected="selectedTypeIds.includes(type.id)"
        :class="{ selected: selectedTypeIds.includes(type.id) }"
        @click="toggleType(type)"
        @keydown.enter="toggleType(type)"
      >
        <div class="label-container">
          <label :id="index.toString()" :for="index.toString()">
            {{ getTranslatedName(type) }}
          </label>
          <span style="margin-left: 4px;">({{ type.count }})</span>
        </div>
        <input
          :id="index.toString()"
          type="checkbox"
          :name="type.name"
          :value="type.id"
          :checked="selectedTypeIds.includes(type.id)"
          :aria-describedby="index.toString()"
        >
      </li>
    </div>
    <div
      v-if="types.length > maxVisibleTypes"
      role="button"
      class="toggle-view-button"
      @click="showAll = !showAll"
    >
      {{ showAll ? 'Voir moins' : 'Tout voir' }}
      <svg
        v-if="!showAll"
        width="12"
        height="7"
        viewBox="0 0 12 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="chevron-icon"
      >
        <path
          d="M11 1.3333L6.33338 6L1.66671 1.3333"
          stroke="var(--yb-color-primary)"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <svg
        v-else
        width="12"
        height="7"
        viewBox="0 0 12 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="chevron-icon"
      >
        <path
          d="M11 6L6.33338 1.3333L1.66671 6"
          stroke="var(--yb-color-primary)"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </ul>
</template>

<style lang="css" scoped>
ul {
  margin: 0;
  padding: 0;
}

.options-list {
  list-style-type: none;
  background-color: var(--yb-color-white);
  border-radius: var(--yb-radius-sm);
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: var(--yb-radius-sm);
  outline: none;
  padding-block: var(--yb-spacing-xs);
  padding-right: var(--yb-spacing-xs);
}

.option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  color: var(--yb-color-black);
  cursor: pointer;

  & > * {
    pointer-events: none;
  }

  .label-container {
    display: flex;
    width: 90%;
    gap: var(--yb-spacing-xs);
  }

  label {
    max-width: 80%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  input[type='checkbox'] {
    appearance: none;
    -webkit-appearance: none;
    position: relative;
    width: 16px;
    aspect-ratio: 1;
    border: 1px solid var(--yb-color-mediumgray);
    border-radius: 2px;
    background-color: var(--yb-color-white);
    margin-inline: var(--yb-spacing-xs);
    accent-color: var(--yb-color-primary);
  }

  input[type='checkbox']:checked {
    background-color: var(--yb-color-primary);
    border-color: var(--yb-color-primary);
    color: var(--yb-color-white);
  }

  input[type='checkbox']:checked::before {
    content: '\2713';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    text-align: center;
    color: var(--yb-color-white);
    font-size: 16px;
  }
}

.toggle-view-button {
  display: flex;
  align-items: center;
  gap: var(--yb-spacing-xs);
  background: none;
  border: none;
  color: var(--yb-color-primary);
  padding: 0;
  margin-top: var(--yb-spacing-sm);
  cursor: pointer;
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-semi-bold);
  border-bottom: solid 1px;
}

.option-row:hover,
.option-row:focus,
.option-row:focus-visible {
  background-color: var(--yb-color-lightgray);
}
</style>
